import { useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@components/ui/form";
import { UploadButton } from "@shared-kernel/primary/shared/upload-button/upload-button";
import { Plus, Trash2, User } from "lucide-react";
import type { Nullable } from "@shared-kernel/core/types/nullable";
import type { RequestState } from "@redux/state/shared-types";
import type { RequestDataFormInputs } from "../form-validation/request-data-form.schema";
import { PDF_MAX_FILE_SIZE, PDF_FILETYPE } from "@shared-kernel/domain/types/file-validation";

interface Props {
  onUpload: (rowIndex: number, file: File) => void;
  uploadProcessing: RequestState;
}

export const SpeakerList = ({ onUpload, uploadProcessing }: Props) => {
  const form = useFormContext<RequestDataFormInputs>();
  const {
    control,
    getValues,
    formState: { errors },
  } = form;
  const [activeUploadIndex, setActiveUploadIndex] = useState<Nullable<number>>(null);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "speakers",
  });

  const handleAddSpeaker = () => {
    append({ firstName: "", lastName: "", resumeUrl: "" });
  };

  const handleRemoveSpeaker = (index: number) => {
    // If this slot is currently uploading, cancel it
    if (activeUploadIndex === index) {
      setActiveUploadIndex(null);
    }

    // Adjust activeUploadIndex if deleting a slot before it
    if (activeUploadIndex !== null && index < activeUploadIndex) {
      setActiveUploadIndex(activeUploadIndex - 1);
    }

    remove(index);
  };

  // Le créneau reste désigné après l'envoi : il ne tourne que tant que le téléversement est en cours
  const handleFileSelect = (file: File, index: number) => {
    setActiveUploadIndex(index);
    onUpload(index, file);
  };

  const isUploading = uploadProcessing === "pending";

  return (
    <div className="space-y-4">
      <FormLabel>Intervenants</FormLabel>
      <FormDescription>CV au format PDF uniquement, 10 Mo maximum</FormDescription>

      {fields.length === 0 && (
        <p className="text-sm text-muted-foreground">Aucun intervenant ajouté. Cliquez sur le bouton ci-dessous pour en ajouter un.</p>
      )}

      <div className="space-y-4">
        {fields.map((field, index) => {
          const isThisSlotUploading = activeUploadIndex === index && isUploading;
          const resumeUrl = getValues(`speakers.${index}.resumeUrl`);

          return (
            <div key={field.id} className="rounded-lg border border-muted-foreground/20 p-4">
              <div className="flex items-start gap-4">
                <User className="mt-2 size-5 shrink-0 text-muted-foreground" />
                <div className="flex-1 space-y-3">
                  <div className="grid gap-3 md:grid-cols-2">
                    <FormField
                      control={control}
                      name={`speakers.${index}.firstName`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs text-muted-foreground">Prénom</FormLabel>
                          <FormControl>
                            <Input type="text" {...field} placeholder="Prénom de l'intervenant" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={control}
                      name={`speakers.${index}.lastName`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs text-muted-foreground">Nom</FormLabel>
                          <FormControl>
                            <Input type="text" {...field} placeholder="Nom de l'intervenant" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* CV Upload */}
                  <UploadButton
                    label="CV (PDF)"
                    onFileSelect={file => handleFileSelect(file, index)}
                    accept={PDF_FILETYPE}
                    maxSize={PDF_MAX_FILE_SIZE}
                    value={resumeUrl}
                    processing={isThisSlotUploading ? uploadProcessing : "idle"}
                    error={errors.speakers?.[index]?.resumeUrl?.message}
                    disabled={isUploading && !isThisSlotUploading}
                    buttonText="Ajouter un CV"
                  />
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveSpeaker(index)}
                  type="button"
                  className="text-red-500 hover:text-red-600"
                  disabled={isThisSlotUploading}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {errors.speakers && !Array.isArray(errors.speakers) && errors.speakers.message && (
        <p className="text-sm font-medium text-destructive">{errors.speakers.message}</p>
      )}

      <Button variant="outline" onClick={handleAddSpeaker} type="button" className="w-full" disabled={isUploading}>
        <Plus className="mr-2 size-4" />
        Ajouter un intervenant
      </Button>
    </div>
  );
};
